// Content service for handling contact forms and other content-related API calls
import { API_CONFIG } from '../config/env';

const API_BASE_URL = API_CONFIG.BASE_URL;

class ContentService {
  // Helper method to get admin token from localStorage
  getAdminToken() {
    // Check for adminAuthToken first (from adminLogin.jsx)
    let token = localStorage.getItem('adminAuthToken');
    
    // If not found, check for adminAuth (from authService.js)
    if (!token) {
      const adminAuth = localStorage.getItem('adminAuth');
      if (adminAuth) {
        const authData = JSON.parse(adminAuth);
        token = authData.access_token;
      }
    }
    
    if (!token) {
      throw new Error('Admin not authenticated');
    }
    
    return token;
  }

  async submitContactForm(formData) {
    try {
      const response = await fetch(`${API_BASE_URL}/core/contact/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Extract validation errors if available
        let errorMessage = data.error || 'Failed to submit contact form';
        if (data.details) {
          // Format validation errors
          const errors = Object.entries(data.details)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          errorMessage = errors || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async getContactMessages() {
    try {
      const token = this.getAdminToken();
      
      const response = await fetch(`${API_BASE_URL}/core/contact-messages/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch contact messages');
      }

      // Ensure all IDs are preserved as strings to prevent scientific notation conversion
      const normalizeIds = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(item => normalizeIds(item));
        } else if (obj && typeof obj === 'object') {
          const normalized = {};
          for (const [key, value] of Object.entries(obj)) {
            if (key === 'id' && value != null) {
              // Convert ID to string to preserve UUID format
              normalized[key] = String(value);
            } else if (Array.isArray(value)) {
              normalized[key] = normalizeIds(value);
            } else if (value && typeof value === 'object') {
              normalized[key] = normalizeIds(value);
            } else {
              normalized[key] = value;
            }
          }
          return normalized;
        }
        return obj;
      };

      // Normalize IDs in the response
      if (data.results) {
        data.results = normalizeIds(data.results);
      } else if (Array.isArray(data)) {
        data = normalizeIds(data);
      } else {
        data = normalizeIds(data);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async updateContactMessage(messageId, updateData) {
    try {
      const token = this.getAdminToken();
      // Ensure messageId is converted to string to prevent scientific notation
      let idString = String(messageId);
      
      // If the ID is in scientific notation, we can't recover it - throw a helpful error
      if (idString.includes('e+') || idString.includes('e-') || idString.includes('E+') || idString.includes('E-')) {
        throw new Error('Invalid contact message ID format. Please refresh the page and try again.');
      }
      
      const response = await fetch(`${API_BASE_URL}/core/contact-messages/${idString}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        // Try to parse error response, but handle non-JSON responses
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || errorData.detail || 'Failed to update contact message');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  async assignContactToMe(messageId) {
    try {
      const token = this.getAdminToken();
      // Ensure messageId is converted to string to prevent scientific notation
      let idString = String(messageId);
      
      // If the ID is in scientific notation, we can't recover it - throw a helpful error
      if (idString.includes('e+') || idString.includes('e-') || idString.includes('E+') || idString.includes('E-')) {
        throw new Error('Invalid contact message ID format. Please refresh the page and try again.');
      }
      
      const response = await fetch(`${API_BASE_URL}/core/contact-messages/${idString}/assign_to_me/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Try to parse error response, but handle non-JSON responses
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || errorData.detail || 'Failed to assign contact message');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to assign contact message');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async markAsContacted(messageId) {
    try {
      const token = this.getAdminToken();
      const response = await fetch(`${API_BASE_URL}/core/contact-messages/${messageId}/mark_as_contacted/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to mark as contacted');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }


  async getContactStats() {
    try {
      const token = this.getAdminToken();
      const response = await fetch(`${API_BASE_URL}/core/contact-messages/stats/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch contact stats');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async getHomeContent() {
    try {
      // For now, return mock data since we don't have a home content API
      // This can be replaced with a real API call when needed
      const mockHomeContent = {
        success: true,
        data: {
          hero: {
            title: "Welcome to Nexen Consulting",
            subtitle: "Your Trusted Partner for Tax Services and Business Consulting",
            description: "We provide comprehensive tax preparation, business consulting, and staffing solutions to help your business thrive."
          },
          features: {
            title: "Our Services",
            description: "Comprehensive solutions for all your business needs"
          },
          stats: {
            title: "Why Choose Us",
            description: "Trusted by businesses nationwide"
          }
        }
      };

      return mockHomeContent;
    } catch (error) {
      throw error;
    }
  }

  async getServicesContent() {
    try {
      // Mock data for services page
      const mockServicesContent = {
        success: true,
        data: {
          title: "Our Services",
          description: "Comprehensive solutions for all your business needs",
          services: [
            {
              title: "Tax Preparation",
              description: "Professional tax preparation services for individuals and businesses"
            },
            {
              title: "Business Consulting",
              description: "Strategic business consulting to help your company grow"
            },
            {
              title: "Staffing Solutions",
              description: "Find the right talent for your business needs"
            }
          ]
        }
      };

      return mockServicesContent;
    } catch (error) {
      throw error;
    }
  }

  async getPortfolioContent() {
    try {
      // Mock data for portfolio page
      const mockPortfolioContent = {
        success: true,
        data: {
          title: "Our Portfolio",
          description: "See our successful projects and client testimonials",
          projects: [
            {
              title: "Tax Optimization Project",
              description: "Helped a client save $50,000 in taxes through strategic planning"
            },
            {
              title: "Business Growth Strategy",
              description: "Developed growth strategy that increased revenue by 200%"
            }
          ]
        }
      };

      return mockPortfolioContent;
    } catch (error) {
      throw error;
    }
  }

  async getAboutContent() {
    try {
      // Mock data for about page
      const mockAboutContent = {
        success: true,
        data: {
          title: "Nexen Consulting",
          description: "Your trusted partner for tax services and business consulting",
          team: [
            {
              name: "John Smith",
              position: "CEO & Founder",
              description: "20+ years of experience in tax and business consulting"
            },
            {
              name: "Jane Doe",
              position: "Tax Specialist",
              description: "Certified tax professional with expertise in complex tax situations"
            }
          ],
          stats: {
            clients: "500+",
            years: "15+",
            success: "99%"
          }
        }
      };

      return mockAboutContent;
    } catch (error) {
      throw error;
    }
  }
}

const contentService = new ContentService();
export default contentService;